import { useState } from 'react'
import PropTypes from 'prop-types'
import Button from 'observian-ui/lib/button'
import { useStaticQuery, graphql } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import Modal from 'observian-ui/lib/modal'
import { BlogPostCard } from 'observian-ui/lib/blog-post-card'
import { useQuery } from '@apollo/client'
import CaseStudy from 'observian-ui/lib/caseStudy'
import ContactModal from 'observian-ui/lib/modal/contact'
import TextUnderline from 'components/SVG/textUnderline'
import NodeInsights from 'components/SVG/appdynamics/NodeInsights'
import ReleaseCycle from 'components/SVG/appdynamics/ReleaseCycle'
import CloudServer from 'components/SVG/clouds/CloudServer'
import SingleCell from 'components/SVG/SingleCell'
import Gears from 'components/SVG/gears'
import Particles from 'components/SVG/particles'
import GraphOutline from 'components/SVG/graphOutline'
import ContactForm from 'components/Form/Contact'
import Form from 'components/Form'
import { transformPosts } from 'utils/blogTransform'
import { buildFormUrl } from '../../../utils/formRedirect'

import AppDCiscoLogo from '../../../../static/images/partners/appdynamics-part-of-cisco.png'
import AppDLogo from '../../../../static/images/partners/appdynamics-logo.png'
import Checkmark from '../../../images/pages/appdynamics/checkmark.png'
import { GET_BLOG_POSTS } from '../Blog/queries'

import style from './appdynamics.module.css'

const caseStudies = [
  {
    title: 'DevOps Guide',
    description: 'How to Use APM to Enhance Performance Testing',
    url: 'https://observian-casestudies.s3.amazonaws.com/AppDynamics/devops-guide-how-to-use-apm-to-enhance-performance-testing-Observian.pdf',
  },
  {
    title: 'AppDynamics for SAP',
    description: 'Optimize Performance with Code-Level Visibility and Root Cause Analysis',
    url: 'https://observian-casestudies.s3.amazonaws.com/AppDynamics/SAP+Data+Sheet-042020-Observian.pdf',
  },
  {
    title: 'Monitoring Hybrid Cloud Applications',
    description: 'The 3 Critical Monitoring Tools for Hybrid Cloud Applications',
    url: 'https://observian-casestudies.s3.amazonaws.com/AppDynamics/the-3-critical-monitoring-tools-for-hybrid-cloud-applications-Observian.pdf',
  },
]
const AppDynamics = ({ members }) => {
  const [schedulerModal, setSchedulerModal] = useState(false)
  const [trialModal, setTrialModal] = useState(false)
  const [isSuccessful, setIsSuccessful] = useState(false)

  const { data: blogPosts } = useQuery(GET_BLOG_POSTS, {
    variables: {
      offset: 0,
      topicIdFilter: '29112093329',
      limit: 10,
    },
    fetchPolicy: 'cache-and-network',
  })
  const posts = blogPosts && blogPosts.allBlogPosts && blogPosts.allBlogPosts.posts
  const topics = blogPosts && blogPosts.allTopics && blogPosts.allTopics.topics
  const authors = blogPosts && blogPosts.allAuthors && blogPosts.allAuthors.authors
  const currentBlogPosts =
    (posts && topics && authors && transformPosts({ posts, authors, members })) || []
  const data = useStaticQuery(graphql`
    {
      issuesDashboard: file(relativePath: { eq: "pages/appdynamics/issues-dashboard.png" }) {
        childImageSharp {
          gatsbyImageData(width: 580, layout: CONSTRAINED)
        }
      }
      laptop: file(relativePath: { eq: "pages/appdynamics/laptop.png" }) {
        childImageSharp {
          gatsbyImageData(width: 540, layout: CONSTRAINED)
        }
      }
      avatar: file(relativePath: { eq: "pages/appdynamics/scottbarnhill.jpg" }) {
        childImageSharp {
          gatsbyImageData(width: 150, layout: FIXED)
        }
      }
      avatarCompanyLogo: file(
        relativePath: { eq: "pages/appdynamics/default-avatar-company-logo.png" }
      ) {
        childImageSharp {
          gatsbyImageData(width: 260, layout: FIXED)
        }
      }
    }
  `)

  return (
    <section className={style.appdynamics}>
      <div className={style.hero}>
        <span role="presentation" alt="Hero Background Left" className={style.topLeftBG} />
        <span role="presentation" alt="Hero Background Right" className={style.bottomRightBG} />
        <SingleCell className={style.heroSingleCell} />
        <SingleCell className={style.heroSingleCellTop} />
        <div className={style.heroContainer}>
          <img className={style.heroBGImage} src={AppDLogo} alt="AppDynamics Logo" />
          <NodeInsights
            className={style.nodeInsights}
            classNames={{ container: style.nodeInsightsContainer }}
          />
          <div className={style.heroContent}>
            <svg
              className={style.particles}
              xmlns="http://www.w3.org/2000/svg"
              width="506"
              height="310"
              viewBox="0 0 506 310"
            >
              <defs>
                <linearGradient id="appd-particles-a" x1="0%" x2="102%" y1="18.927%" y2="81.694%">
                  <stop offset="0%" stopColor="#FFC42A" />
                  <stop offset="100%" stopColor="#FF0061" stopOpacity=".946" />
                </linearGradient>
                <linearGradient id="appd-particles-b" x1="0%" x2="102%" y1="16.136%" y2="84.541%">
                  <stop offset="0%" stopColor="#FFC42A" />
                  <stop offset="100%" stopColor="#FF0061" stopOpacity=".946" />
                </linearGradient>
                <linearGradient id="appd-particles-c" x1="0%" x2="102%" y1="16.136%" y2="84.541%">
                  <stop offset="0%" stopColor="#FFC42A" />
                  <stop offset="100%" stopColor="#FF0061" stopOpacity=".946" />
                </linearGradient>
                <linearGradient id="appd-particles-d" x1="0%" x2="102%" y1="16.136%" y2="84.541%">
                  <stop offset="0%" stopColor="#FFC42A" />
                  <stop offset="100%" stopColor="#FF0061" stopOpacity=".946" />
                </linearGradient>
                <linearGradient id="appd-particles-e" x1="0%" x2="102%" y1="0%" y2="101%">
                  <stop offset="0%" stopColor="#FFC42A" />
                  <stop offset="100%" stopColor="#FF0061" stopOpacity=".946" />
                </linearGradient>
              </defs>
              <g fill="none" fillRule="evenodd" opacity=".255" transform="translate(2 2)">
                <polygon
                  fill="url(#appd-particles-a)"
                  stroke="url(#appd-particles-a)"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="3"
                  points="10.428 276.054 19.391 290.185 1.466 290.185"
                  transform="rotate(-13 10.428 283.12)"
                />
                <polygon
                  fill="url(#appd-particles-b)"
                  stroke="url(#appd-particles-b)"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="3"
                  points="390.391 247.054 397.316 258.452 383.466 258.452"
                  transform="rotate(20 390.39 252.753)"
                />
                <polygon
                  fill="url(#appd-particles-c)"
                  stroke="url(#appd-particles-c)"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="3"
                  points="385.127 31.054 388.789 37.081 381.466 37.081"
                  transform="rotate(-58 385.127 34.067)"
                />
                <polygon
                  fill="url(#appd-particles-d)"
                  stroke="url(#appd-particles-d)"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="3"
                  points="494.808 2.054 500.151 10.847 489.466 10.847"
                  transform="rotate(-26 494.808 6.45)"
                />
                <circle cx="469.156" cy="75.744" r="2.69" fill="url(#appd-particles-e)" />
                <circle cx="221.156" cy="194.744" r="2.69" fill="url(#appd-particles-e)" />
                <circle cx="241.156" cy="304.744" r="2.69" fill="url(#appd-particles-e)" />
              </g>
            </svg>

            <img
              className={style.heroImage}
              src={AppDCiscoLogo}
              alt="AppDynamics Apart of Cisco Logo"
            />
            <p className={style.heroText}>
              Observian has partnered with AppDynamics to bring our customers real-time, AI-powered
              visual performance monitoring. Check on the health of your business at a glance with
              AppDynamics. Learn how AppDynamics can transform your business.
            </p>
            <Button
              className={style.heroButton}
              variant="primary"
              label="Get a Demo"
              onClick={() => window.location.href = buildFormUrl([
                {
                  key: 'CTA',
                  value: `AppDynamics - Get a Demo`
                }
              ])}
            />
            {trialModal ? (
              <Modal isTransparent isOpen={trialModal} onClose={() => setTrialModal(false)}>
                <ContactModal
                  showClose
                  shouldCloseOnOverlayClick
                  onClose={() => setTrialModal(false)}
                  onDismiss={() => setTrialModal(false)}
                  isOpen={trialModal}
                  closeButtonPosition="right"
                  ariaHideApp={false}
                  success={isSuccessful}
                >
                  <ContactForm
                    showLogo
                    headerTitle="Contact Us"
                    buttonLabel="Send"
                    ctaIntentSuffix="modal-appdynamics-trial"
                    inquiryPlaceholder="I'd like to learn more about AppDynamics."
                    isSuccessfulCallback={(v) => {
                      setIsSuccessful(v)
                      if (!v) {
                        setTrialModal(false)
                      }
                    }}
                  />
                </ContactModal>
              </Modal>
            ) : null}
          </div>
        </div>
      </div>
      <div className={style.identifyResolve}>
        <div className={style.identifyResolveContainer}>
          <div className={style.identifyResolveContent}>
            <h2 className={style.identifyResolveTitle}>
              <span className={style.identify}>Identify</span> and{' '}
              <span className={style.resolve}>resolve</span> issues before they impact customers
            </h2>
            <TextUnderline />
            <p className={style.identifyText}>
              AppDynamics enables businesses through real-time app performance monitoring. Powerful
              machine learning algorithms provide deep insights into every aspect of your business.
              Quickly visualize end-to-end performance data in complex, multi-cloud environments.
              Intelligent anomaly detection flags performance issues faster and highlights the exact
              source. Instant root cause diagnostics allows IT teams to resolve issues quicker than
              ever. Get ahead of your performance issues with AppDynamics.
            </p>
            <Button
              className={style.identifyButton}
              variant="primary"
              label="Schedule a Conversation"
              onClick={() => window.location.href = buildFormUrl([
                {
                  key: 'CTA',
                  value: `AppDynamics - Schedule a Conversation`
                }
              ])}
            />

            {schedulerModal ? (
              <Modal
                isTransparent
                isOpen={schedulerModal}
                onClose={(e) => setSchedulerModal(false)}
              >
                <ContactModal
                  showClose
                  shouldCloseOnOverlayClick
                  onClose={() => setSchedulerModal(false)}
                  onDismiss={() => setSchedulerModal(false)}
                  isOpen={schedulerModal}
                  closeButtonPosition="right"
                  ariaHideApp={false}
                  success={isSuccessful}
                >
                  <ContactForm
                    showLogo
                    headerTitle="Contact Us"
                    buttonLabel="Send"
                    ctaIntentSuffix="modal-aws-partners-benefits"
                    inquiryPlaceholder="I'd like to schedule a call with Observian."
                    isSuccessfulCallback={(v) => {
                      setIsSuccessful(v)
                      if (!v) {
                        setSchedulerModal(false)
                      }
                    }}
                  />
                </ContactModal>
              </Modal>
            ) : null}
          </div>
          <div className={style.imageContainer}>
            <GatsbyImage
              image={data.issuesDashboard.childImageSharp.gatsbyImageData}
              className={style.identifyImage}
              alt="AppDynamics Dashboard"
            />
            <img className={style.identifyCheckmark} src={Checkmark} alt="Checkmark" />
          </div>
        </div>
      </div>
      <div className={style.businessInsights}>
        <div className={style.businessInsightsContainer}>
          <SingleCell className={style.singleCell} />
          <Gears className={style.gears} />
          <Particles className={style.particles} />
          <GraphOutline className={style.graphOutline} />
          <div className={style.businessInsightsImageContainer}>
            <GatsbyImage
              image={data.laptop.childImageSharp.gatsbyImageData}
              className={style.businessInsightsImage}
              alt="AppDynamics Dashboard Laptop"
            />
          </div>
          <div className={style.businessInsightsContent}>
            <h2 className={style.businessInsightsTitle}>
              Get Immediate Business Insights from Performance Data
            </h2>
            <TextUnderline />
            <p className={style.businessInsightsText}>
              Business IQ translates performance monitoring data into clear business insights that
              can be used across your organization. Business and IT colleagues collaborate using a
              unified platform to identify hurdles throughout the customer journey. Quickly pinpoint
              performance issues that are affecting business outcomes, using data that everyone can
              understand. Transform your business using AppDynamics insights that were previously
              lost in silos.
            </p>
          </div>
        </div>
        <svg
          className={style.divderLaserTop}
          xmlns="http://www.w3.org/2000/svg"
          width="831"
          height="264"
          viewBox="0 0 831 264"
        >
          <defs>
            <linearGradient
              id="divider-laser-top-a"
              x1="15.436%"
              x2="114.319%"
              y1="51.24%"
              y2="49.125%"
            >
              <stop offset="0%" stopColor="#FFC42A" />
              <stop offset="100%" stopColor="#FF0061" stopOpacity=".946" />
            </linearGradient>
            <filter
              id="divider-laser-top-b"
              width="107.9%"
              height="145.4%"
              x="-4%"
              y="-22.7%"
              filterUnits="objectBoundingBox"
            >
              <feGaussianBlur in="SourceGraphic" stdDeviation="10" />
            </filter>
            <filter
              id="divider-laser-top-c"
              width="107.9%"
              height="145.4%"
              x="-4%"
              y="-22.7%"
              filterUnits="objectBoundingBox"
            >
              <feGaussianBlur in="SourceGraphic" stdDeviation="10" />
            </filter>
          </defs>
          <g fill="url(#divider-laser-top-a)" fillRule="evenodd" transform="translate(33 32)">
            <polygon points=".536 133.09 224.163 35.39 465.148 75.238 756.339 .863 423.969 103.031 230.02 50.396" />
            <polygon
              points="4.527 166 228.154 68.3 502.632 105.218 760.33 33.774 428.699 148.315 234.011 83.307"
              filter="url(#divider-laser-top-b)"
              opacity=".249"
              transform="rotate(-5 382.429 99.887)"
            />
            <polygon
              points="4.527 166 228.154 68.3 502.632 105.218 760.33 33.774 428.699 148.315 234.011 83.307"
              filter="url(#divider-laser-top-c)"
              opacity=".249"
              transform="rotate(-5 382.429 99.887)"
            />
          </g>
        </svg>
        <svg
          className={style.divderLaserFirst}
          xmlns="http://www.w3.org/2000/svg"
          width="482"
          height="213"
          viewBox="0 0 482 213"
        >
          <defs>
            <linearGradient
              id="divider-laser-2-a"
              x1="12.453%"
              x2="87.895%"
              y1="51.133%"
              y2="48.907%"
            >
              <stop offset="0%" stopColor="#1D8DBA" />
              <stop offset="100%" stopColor="#03ECAE" />
            </linearGradient>
            <filter
              id="divider-laser-2-b"
              width="114.8%"
              height="182.2%"
              x="-7.4%"
              y="-41.1%"
              filterUnits="objectBoundingBox"
            >
              <feGaussianBlur in="SourceGraphic" stdDeviation="10" />
            </filter>
          </defs>
          <g fill="url(#divider-laser-2-a)" fillRule="evenodd" transform="translate(35 35)">
            <polygon
              points="4 107.952 191.943 35 408.943 52.769 166.757 57.65"
              filter="url(#divider-laser-2-b)"
              opacity=".511"
              transform="rotate(-10 206.471 71.476)"
            />
            <polygon points="4 107.952 191.943 35 408.943 52.769 166.757 57.65" />
          </g>
        </svg>
        <svg
          className={style.divderLaser}
          xmlns="http://www.w3.org/2000/svg"
          width="838"
          height="196"
          viewBox="0 0 838 196"
        >
          <defs>
            <linearGradient
              id="divider-laser-a"
              x1="12.453%"
              x2="87.895%"
              y1="50.361%"
              y2="49.652%"
            >
              <stop offset="0%" stopColor="#1D8DBA" />
              <stop offset="100%" stopColor="#03ECAE" />
            </linearGradient>
            <filter
              id="divider-laser-b"
              width="107.8%"
              height="176.7%"
              x="-3.9%"
              y="-38.4%"
              filterUnits="objectBoundingBox"
            >
              <feGaussianBlur in="SourceGraphic" stdDeviation="10" />
            </filter>
          </defs>
          <g fill="url(#divider-laser-a)" fillRule="evenodd" transform="translate(32 32)">
            <polygon
              points="2.349 42.091 218.437 26.988 343.58 61.035 620.811 82.336 771.363 61.035 591.8 105.213 363.58 71.833 210.597 42.091"
              filter="url(#divider-laser-b)"
              opacity=".74"
              transform="rotate(-4 386.856 66.1)"
            />
            <polygon points="2 32.103 218.088 17 343.231 51.047 620.462 72.348 771.014 51.047 591.451 95.225 363.231 61.845 210.248 32.103" />
          </g>
        </svg>
      </div>
      <div className={style.info}>
        <div className={style.infoContainer}>
          <div className={style.infoContent}>
            <div className={style.infoHeader}>
              <svg
                className={style.infoIcon}
                xmlns="http://www.w3.org/2000/svg"
                width="80"
                height="80"
                viewBox="0 0 80 80"
              >
                <defs>
                  <linearGradient
                    id="user-icon-a"
                    x1="12.453%"
                    x2="87.895%"
                    y1="84.924%"
                    y2="16.33%"
                  >
                    <stop offset="0%" stopColor="#1D8DBA" />
                    <stop offset="100%" stopColor="#03ECAE" />
                  </linearGradient>
                </defs>
                <path
                  fill="url(#user-icon-a)"
                  fillRule="evenodd"
                  d="M70.0002734,80 L10.0006641,80 C4.4787675,80 0,75.5248438 0,70.0025 L0,62.50125 C0,58.4985938 3.05548069,54.845 8.14878183,51.9117188 C8.54190837,51.62125 9.01847273,51.4478125 9.53972476,51.4478125 C10.8403548,51.4478125 11.8931715,52.5 11.8931715,53.80125 C11.8931715,54.6740625 11.4103571,55.4271875 10.7053543,55.8325 L10.7114481,55.8421875 C7.11252778,57.8160938 5.00001953,60.1207813 5.00001953,62.50125 L5.00001953,70.0025 C5.00001953,72.7625 7.23815327,75 10.0006641,75 L70.0002734,75 C72.7615342,75 75.000293,72.7625 75.000293,70.0025 L75.000293,62.50125 C75.000293,55.598125 59.3288255,50.0025 40.0014063,50.0025 C35.4042008,50.0025 31.1829343,48.4339063 27.8022961,45.8264063 C25.4774433,44.0295313 23.5621233,41.7334375 22.2066492,39.1003125 C22.1358677,38.9610938 22.07118,38.826875 22.0071172,38.6876563 C21.7653975,38.1859375 21.5402404,37.6732813 21.3405521,37.1520313 C21.2660206,36.9653125 21.195239,36.7723438 21.1311763,36.5820313 C20.9572694,36.0925 20.8089875,35.5871875 20.677112,35.079375 C20.6221118,34.869375 20.5611741,34.660625 20.5128926,34.4446875 C20.3938297,33.9234375 20.3035168,33.3948438 20.2260165,32.8578125 C20.1972664,32.6515625 20.155235,32.45125 20.1332036,32.245 C20.0489846,31.5076563 20.0007031,30.761875 20.0007031,30.0014063 L20.0007031,15.0014063 C20.0007031,6.71625 26.7169794,0 35.0013867,0 L44.9989258,0 C53.2827081,0 59.9989844,6.71625 59.9989844,15.00125 L59.9989844,30.00125 C59.9989844,33.4534375 59.0724183,36.66875 57.5294435,39.4959375 C57.2144422,40.5103125 56.2769386,41.25125 55.1563092,41.25125 C53.7756788,41.25125 52.6550494,40.133125 52.6550494,38.7525 C52.6550494,38.3301563 52.7709874,37.9382812 52.9541131,37.5903125 L52.924738,37.574375 C54.0648987,35.6260938 54.7863078,33.404375 54.9474021,31.0253125 C54.982871,30.6871875 54.9986523,30.3453125 54.9986523,30.0010938 L54.9986523,15.0010938 C54.9986523,9.47859375 50.5234786,4.99984375 44.9986133,4.99984375 L35.0010742,4.99984375 C29.4762089,4.99984375 25.0010352,9.47859375 25.0010352,15.0010938 L25.0010352,30.0010938 C25.0010352,30.3453125 25.0169727,30.6871875 25.0522854,31.0253125 C25.5643186,38.6070312 31.7105926,44.6373438 39.3412474,44.969375 C39.5407795,44.9925 39.7568741,45.0010938 40.0010938,45.0010938 C44.2553291,45.0010938 48.3495639,45.2940625 52.1947351,45.8323437 C53.185989,45.9703125 54.1552115,46.1253125 55.1122465,46.29625 C55.1366216,46.29875 55.1634967,46.3059375 55.1855281,46.3084375 C56.1035004,46.4732812 57.0019414,46.6564062 57.8833511,46.8492187 C57.9871015,46.8723437 58.0860081,46.894375 58.1897586,46.9176562 C59.9414841,47.3107812 61.6211782,47.7575 63.2129032,48.253125 C63.2897785,48.28 63.3704038,48.2982812 63.4447791,48.3239062 C64.2199384,48.5692187 64.9768163,48.8304687 65.7104129,49.1003125 C65.7885382,49.1296875 65.8654135,49.1589062 65.9422888,49.1870312 C66.634479,49.4445312 67.3069817,49.711875 67.9588592,49.9926562 C68.0126094,50.0182812 68.0674534,50.0403125 68.1247974,50.0634375 C68.8083938,50.3589062 69.4615213,50.6615625 70.0926175,50.9778125 C70.1732429,51.015625 70.2501182,51.0584375 70.3269935,51.09625 C70.9519959,51.4123437 71.5610608,51.7407812 72.133563,52.0789062 C72.1470006,52.085 72.1604381,52.0984375 72.1751257,52.1045312 C72.7232528,52.4292187 73.2323173,52.76125 73.7279442,53.099375 C73.8146633,53.1567187 73.90857,53.2214062 73.995289,53.2825 C74.490916,53.6340625 74.9609178,53.988125 75.3990445,54.351875 C75.446701,54.3909375 75.4954512,54.4325 75.5406076,54.4715625 C75.9715468,54.8378125 76.3804546,55.2089062 76.7540498,55.5884375 C76.7735811,55.6079687 76.7931125,55.6239062 76.8113938,55.6434375 C77.1751452,56.0170312 77.4974902,56.4003125 77.8038977,56.7859375 C77.8710854,56.87625 77.9454607,56.9592187 78.0101485,57.0496875 C78.2957746,57.4329687 78.5570256,57.8223437 78.7865578,58.2154687 C78.8109329,58.2642187 78.840308,58.311875 78.8671831,58.363125 C79.0795277,58.7440625 79.2553096,59.1296875 79.4104664,59.5190625 C79.4335915,59.5776562 79.4629667,59.635 79.481248,59.6935937 C79.6362486,60.105 79.7485928,60.5235938 79.8364056,60.9460938 C79.8510932,61.0267188 79.8645307,61.1096875 79.8779683,61.1939063 C79.9512498,61.6248437 80,62.059375 80,62.50125 L80,70.0025 C80,75.5248438 75.5251388,80 70.0002734,80 L70.0002734,80 Z M22.4994629,50.0025 C22.4994629,51.383125 21.3819585,52.5 20.0007031,52.5 C18.6194477,52.5 17.4988184,51.383125 17.4988184,50.0025 C17.4988184,48.620625 18.6194477,47.5 20.0007031,47.5 C21.3819585,47.5 22.4994629,48.620625 22.4994629,50.0025 L22.4994629,50.0025 Z"
                />
              </svg>
              <h3>Performance from your Customers’ Perspective</h3>
            </div>
            <TextUnderline />
            <p className={style.infoText}>
              End-User Experience Monitoring provides deep visual insights into customer interaction
              and behavior. Every swipe, tap or click is captured to develop a complete
              understanding of your customer journey. Intelligent baselining learns customer
              behaviour on your app or website and quickly notices when something isn’t right.
              Proactively identify and resolve performance bottlenecks before they affect your
              revenue with AppDynamics.
            </p>
          </div>
          <div className={style.infoContent}>
            <div className={style.infoHeader}>
              <svg
                className={style.infoIcon}
                xmlns="http://www.w3.org/2000/svg"
                width="96"
                height="65"
                viewBox="0 0 96 65"
              >
                <defs>
                  <linearGradient
                    id="exchange-icon-a"
                    x1="12.453%"
                    x2="87.895%"
                    y1="66.507%"
                    y2="34.086%"
                  >
                    <stop offset="0%" stopColor="#1D8DBA" />
                    <stop offset="100%" stopColor="#03ECAE" />
                  </linearGradient>
                </defs>
                <path
                  fill="url(#exchange-icon-a)"
                  d="M91.2103267,49.4670312 L79.0429545,61.6227699 C76.9575994,63.7059091 74.2678551,64.8066619 71.5374858,64.9495881 C71.3592898,64.9798722 71.15875,65 70.9085369,65 C67.6452415,65 64.999446,62.3542045 64.999446,59.0909091 L64.999446,56.1363636 C64.999446,54.5047159 63.6765483,53.1818182 62.0449006,53.1818182 L38.4085369,53.1818182 C36.7768892,53.1818182 35.4539915,51.8589205 35.4539915,50.2272727 C35.4539915,48.595625 36.7768892,47.2727273 38.4085369,47.2727273 L63.5221733,47.2727273 C67.6012926,47.2727273 70.9085369,50.579233 70.9085369,54.6590909 L70.9085369,56.0901989 C70.9085369,57.3135653 71.9003409,58.306108 73.124446,58.306108 C73.6834091,58.306108 74.1884517,58.092642 74.5778977,57.7521307 C74.6997727,57.6483523 74.8325426,57.564517 74.9472159,57.4505824 L86.995483,45.403054 C89.213608,43.1841903 89.213608,39.5877699 86.995483,37.3703835 L74.9472159,25.3213778 C74.7588636,25.1337642 74.5498295,24.983821 74.3433807,24.8279688 C73.9899432,24.5899432 73.5838778,24.4211648 73.1242614,24.4211648 C71.9001563,24.4211648 70.9083523,25.4137074 70.9083523,26.6370739 L70.9083523,28.0681818 C70.9083523,32.1480398 67.601108,35.4545455 63.5219886,35.4545455 L38.4083523,35.4545455 L36.9310795,35.4545455 L32.4992614,35.4545455 C30.8676136,35.4545455 29.5447159,36.7774432 29.5447159,38.4090909 L29.5447159,41.3636364 C29.5447159,44.6269318 26.8989205,47.2727273 23.635625,47.2727273 C23.3854119,47.2727273 23.1848722,47.2525994 23.0066761,47.2208381 C20.2764915,47.0793892 17.5865625,45.9786364 15.5012074,43.8940199 L3.33383523,31.7397585 C1.09539773,29.502983 -2.10157484e-07,26.5711506 -2.10157484e-07,23.6391335 C-2.10157484e-07,20.7052699 1.09410511,17.7714062 3.3343892,15.5331534 L15.5017614,3.37889205 C17.5871165,1.29427557 20.2761222,0.195 23.0064915,0.0520738636 C23.1846875,0.0203125 23.3852273,0.000184659091 23.636179,0.000184659091 C26.8994744,0.000184659091 29.5452699,2.64598011 29.5452699,5.90927557 L29.5452699,8.86382102 C29.5452699,10.4954688 30.8681676,11.8183665 32.4998153,11.8183665 L41.3634517,11.8183665 C42.9950994,11.8183665 44.3179972,13.1412642 44.3179972,14.7729119 C44.3179972,16.4045597 42.9950994,17.7274574 41.3634517,17.7274574 L31.0225426,17.7274574 C26.9434233,17.7274574 23.636179,14.4209517 23.636179,10.3410938 L23.636179,8.9099858 C23.636179,7.68661932 22.644375,6.6940767 21.4202699,6.6940767 C20.8620455,6.6940767 20.3562642,6.90754261 19.9668182,7.24805398 C19.8449432,7.35183239 19.7129119,7.43566761 19.5975,7.54960227 L7.54923295,19.5971307 C5.33110795,21.8159943 5.33110795,25.4124148 7.54923295,27.6298011 L19.5975,39.6788068 C19.7858523,39.8664205 19.9948864,40.0163636 20.2013352,40.1722159 C20.5547727,40.4117188 20.9608381,40.5790199 21.4204545,40.5790199 C22.6445597,40.5790199 23.6363636,39.5864773 23.6363636,38.3631108 L23.6363636,36.9318182 C23.6363636,32.8519602 26.943608,29.5454545 31.0227273,29.5454545 L38.4090909,29.5454545 L47.2727273,29.5454545 L62.0454545,29.5454545 C63.6771023,29.5454545 64.9999998,28.2225568 64.9999998,26.5909091 L64.9999998,23.6363636 C64.9999998,20.3730682 67.6457955,17.7272727 70.9090909,17.7272727 C71.159304,17.7272727 71.3598437,17.7474006 71.5380398,17.7791619 C74.2682244,17.9219034 76.9581534,19.0213636 79.0435085,21.1059801 L91.2108807,33.2602415 C93.4504261,35.4977557 94.5454545,38.430696 94.5454545,41.3636364 C94.5454545,44.2965767 93.4504261,47.229517 91.2103267,49.4670312 Z M53.1814489,17.7272727 C51.5498011,17.7272727 50.2269034,16.404375 50.2269034,14.7727273 C50.2269034,13.1410795 51.5498011,11.8181818 53.1814489,11.8181818 C54.8130966,11.8181818 56.1359943,13.1410795 56.1359943,14.7727273 C56.1359943,16.404375 54.8130966,17.7272727 53.1814489,17.7272727 Z"
                />
              </svg>
              <h3>Discover How Your Infrastructure Affects Transactions</h3>
            </div>
            <TextUnderline />
            <p className={style.infoText}>
              Business IQ translates performance monitoring data into clear business insights that
              can be used across your organization. Business and IT colleagues collaborate using a
              unified platform to identify hurdles throughout the customer journey. Quickly pinpoint
              performance issues that are affecting business outcomes, using data that everyone can
              understand. Transform your business using AppDynamics insights that were previously
              lost in silos.
            </p>
          </div>
        </div>
      </div>
      <div className={style.quote}>
        <div className={style.quoteContainer}>
          <div className={style.quoteImage}>
            <GatsbyImage
              image={data.avatar.childImageSharp.gatsbyImageData}
              className={style.avatar}
              alt="Scott Barnhill Avatar"
            />
          </div>
          <div className={style.quoteContent}>
            <svg
              className={style.quotemark}
              xmlns="http://www.w3.org/2000/svg"
              width="41"
              height="46"
              viewBox="0 0 41 46"
            >
              <defs>
                <linearGradient
                  id="quote-marks-a"
                  x1="43.202%"
                  x2="56.861%"
                  y1="84.924%"
                  y2="16.33%"
                >
                  <stop offset="0%" stopColor="#1D8DBA" />
                  <stop offset="100%" stopColor="#03ECAE" />
                </linearGradient>
              </defs>
              <g fill="url(#quote-marks-a)" fillRule="evenodd">
                <polygon points="9.115 0 0 12.137 12.301 43.092 9.115 20.182 18.336 12.137 17.558 2.765" />
                <polygon points="31.115 2 22 14.137 34.301 45.092 31.115 22.182 40.336 14.137 39.558 4.765" />
              </g>
            </svg>

            <p className={style.quoteText}>
              AppDynamics has really brought clarity to the performance of the NetDocuments platform
              and ultimately improved the experience of our customers. By gaining real time insights
              to critical platform and sub-system performance, we are able to spend time where it
              matters most.
            </p>
            <svg
              className={style.quotemarkRight}
              xmlns="http://www.w3.org/2000/svg"
              width="41"
              height="46"
              viewBox="0 0 41 46"
            >
              <defs>
                <linearGradient
                  id="quotemarks-left-a"
                  x1="43.202%"
                  x2="56.861%"
                  y1="84.924%"
                  y2="16.33%"
                >
                  <stop offset="0%" stopColor="#1D8DBA" />
                  <stop offset="100%" stopColor="#03ECAE" />
                </linearGradient>
              </defs>
              <g fill="url(#quotemarks-left-a)" fillRule="evenodd">
                <polygon
                  points="31.115 0 22 12.137 34.301 43.092 31.115 20.182 40.336 12.137 39.558 2.765"
                  transform="matrix(-1 0 0 1 62.336 0)"
                />
                <polygon
                  points="9.115 2 0 14.137 12.301 45.092 9.115 22.182 18.336 14.137 17.558 4.765"
                  transform="matrix(-1 0 0 1 18.336 0)"
                />
              </g>
            </svg>

            <div className={style.quoteUser}>
              <h3 className={style.quoteName}>Scott Barnhill</h3>
              <span className={style.quoteTitle}>SVP Global Cloud Operations</span>
            </div>
            <div className={style.quoteUserCompanyLogo}>
              <GatsbyImage
                image={data.avatarCompanyLogo.childImageSharp.gatsbyImageData}
                className={style.avatarCompanyLogo}
                alt="Net Documents Logo"
              />
            </div>
          </div>
        </div>
      </div>
      <div className={style.cloudMigrationParent}>
        <div className={style.cloudMigration}>
          <div className={style.cloudMigrationContainer}>
            <div className={style.cloudMigrationContent}>
              <h2 className={style.cloudMigrationTitle}>
                Complete Your Cloud Migration with Confidence
              </h2>
              <TextUnderline />
              <p className={style.cloudMigrationText}>
                Don’t risk business-impacting performance problems during your cloud migration. Get
                complete visibility of every application dependency and performance requirement
                before you begin. Use your current application resources to accurately assess your
                cloud server requirements. Compare performance data at every milestone and proceed
                with complete confidence. Prove that your cloud migration will be successful with
                AppDynamics.
              </p>
            </div>
            <CloudServer className={style.cloudServer} />
          </div>
        </div>
      </div>
      <div className={style.multiContainer}>
        <div className={style.releaseCycle}>
          <div className={style.releaseCycleContainer}>
            <ReleaseCycle
              className={style.releaseCycleImage}
              classNames={{ container: style.releaseCycleImageContainer }}
            />
            <div className={style.releaseCycleContent}>
              <h2 className={style.releaseCycleTitle}>Make Monitoring a First Class Citizen</h2>
              <TextUnderline />
              <p className={style.releaseCycleText}>
                Watch a recent webinar we did with AppDynamics on the importance of a reliable
                monitoring solution and how to create quality gates with the Harness.io integration.
              </p>
              <Button
                className={style.releaseCycleButton}
                variant="primary"
                label="Watch On-Demand"
                href="/blog/make-monitoring-a-first-class-citizen"
              />
            </div>
          </div>
        </div>
        <div className={style.caseStudies}>
          {caseStudies.map(({ title, description, url }, index) => (
            <CaseStudy
              displayBorder
              key={`case:study:${index + 1}`}
              title={title}
              description={description}
              url={url}
            />
          ))}
        </div>
        {/* <div className={style.appdynamicsBlogs}>
          <div className={style.blogContainer}>
            <div className={style.blogContent}>
              <div className={style.header}>
                <h3 className={style.title}>Recent Blogs Related to AppDynamics</h3>
              </div>
              <div className={style.blogPosts}>
                {currentBlogPosts.slice(0, 2).map((post) => (
                  <BlogPostCard
                    light
                    key={`blog:post:${post.postId}`}
                    post={post}
                    topics={topics}
                    avatarProps={{ forceAspectRatio: post.blogAuthor.isExternalAuthor }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </section>
  )
}

AppDynamics.propTypes = {
  members: PropTypes.array.isRequired,
}

export default AppDynamics
